import React, { createContext, useContext, useState, useEffect } from 'react';
import auditLogger from '../utils/auditLogger';
import { useAuth } from '../auth/AuthContext';

const CompetitionContext = createContext();

export const useCompetition = () => {
  const context = useContext(CompetitionContext);
  if (!context) {
    throw new Error('useCompetition must be used within a CompetitionProvider');
  }
  return context;
};

export const CompetitionProvider = ({ children }) => {
  const { user } = useAuth();
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Check for stored competition data on component mount
  useEffect(() => {
    const storedCompetition = localStorage.getItem('selectedCompetition');
    if (storedCompetition) {
      try {
        setSelectedCompetition(JSON.parse(storedCompetition));
      } catch (error) {
        console.error('Error parsing stored competition data:', error);
        localStorage.removeItem('selectedCompetition');
      }
    }
    setLoading(false);
  }, []);

  const selectCompetition = (competition) => {
    setSelectedCompetition(competition);
    localStorage.setItem('selectedCompetition', JSON.stringify(competition));

    // Log competition selection
    if (user && competition) {
      auditLogger.logSelectCompetition(
        user.username || user.id,
        competition.name,
        competition.id
      );
    }
  };

  const clearCompetition = () => {
    setSelectedCompetition(null);
    localStorage.removeItem('selectedCompetition');
  };

  // Fetch all competitions
  const fetchCompetitions = async () => {
    try {
      console.log('Fetching competitions from API...');
      const response = await fetch('https://chappie-demo.novosense.africa:6003/api/wdsf/competitions');
      console.log('Competitions response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Competitions data received:', data);
        setCompetitions(data);
        return { success: true, competitions: data };
      } else {
        console.log('Failed to fetch competitions, status:', response.status);
        return { success: false, error: 'Failed to fetch competitions' };
      }
    } catch (error) {
      console.error('Error fetching competitions:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  // Upload image to storage
  const uploadImage = async (file) => {
    try {
      console.log('Uploading image:', file.name);

      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch('https://chappie-demo.novosense.africa:6003/api/wdsf/upload-image', {
        method: 'POST',
        body: formData,
      });

      console.log('Image upload response status:', response.status);

      if (response.ok) {
        const uploadResult = await response.json();
        console.log('Image uploaded successfully:', uploadResult);
        return { success: true, publicUrl: uploadResult.publicUrl };
      } else {
        const errorData = await response.json();
        console.log('Failed to upload image:', errorData);
        return { success: false, error: errorData.error || 'Failed to upload image' };
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  // Create new competition
  const createCompetition = async (competitionData) => {
    try {
      console.log('Creating competition with data:', competitionData);

      const response = await fetch('https://chappie-demo.novosense.africa:6003/api/wdsf/competitions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(competitionData),
      });

      console.log('Create competition response status:', response.status);

      if (response.ok) {
        const newCompetition = await response.json();
        console.log('Competition created successfully:', newCompetition);
        setCompetitions(prev => [...prev, newCompetition]);

        // Log competition creation
        if (user) {
          auditLogger.logCreateCompetition(
            user.username || user.id,
            newCompetition.name,
            newCompetition.id
          );
        }

        return { success: true, competition: newCompetition };
      } else {
        const errorData = await response.json();
        console.log('Failed to create competition:', errorData);
        return { success: false, error: errorData.error || 'Failed to create competition' };
      }
    } catch (error) {
      console.error('Error creating competition:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  // Update existing competition
  const updateCompetition = async (competitionId, competitionData) => {
    try {
      console.log('Updating competition:', competitionId, 'with data:', competitionData);

      const response = await fetch(`https://chappie-demo.novosense.africa:6003/api/wdsf/competitions/${competitionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(competitionData),
      });

      console.log('Update competition response status:', response.status);

      if (response.ok) {
        const updatedCompetition = await response.json();
        console.log('Competition updated successfully:', updatedCompetition);

        // Update competitions list
        setCompetitions(prev =>
          prev.map(comp =>
            comp.id === competitionId ? { ...updatedCompetition, id: competitionId } : comp
          )
        );

        // Update selected competition if it's the one being edited
        if (selectedCompetition && selectedCompetition.id === competitionId) {
          selectCompetition({ ...updatedCompetition, id: competitionId });
        }

        return { success: true, competition: { ...updatedCompetition, id: competitionId } };
      } else {
        const errorData = await response.json();
        console.log('Failed to update competition:', errorData);
        return { success: false, error: errorData.error || 'Failed to update competition' };
      }
    } catch (error) {
      console.error('Error updating competition:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const value = {
    selectedCompetition,
    competitions,
    loading,
    selectCompetition,
    clearCompetition,
    fetchCompetitions,
    createCompetition,
    updateCompetition,
    uploadImage,
    hasCompetition: !!selectedCompetition,
  };

  return (
    <CompetitionContext.Provider value={value}>
      {children}
    </CompetitionContext.Provider>
  );
};